import React, { useEffect, useMemo, useState } from 'react'
import 'react-phone-number-input/style.css'

import { BodyText } from '@hm-group/fabric-components'
import {
  CheckBoxInput,
  FieldController,
  getIsValidPassword,
  Input,
  PasswordInput,
  Select,
  AddressAutocomplete,
  PhoneNumberInput,
  RectangleSkeleton,
  Collapse,
  InfoIcon,
  PasswordInputLabels,
} from '@pb/components'
import { getLabelFromStorage, getLocale } from '@pb/utils'
import { useDecision } from '@optimizely/react-sdk'
import cn from 'classnames'
import { isEmpty } from 'ramda'
import { RegisterOptions, useFormContext } from 'react-hook-form'
import { useSelector, useDispatch } from 'react-redux'

import { IS_PASSWORD_METER_ENABLED } from 'App/constants'
import { FieldConfig, UseFormContextFixedType } from 'App/duck/types'
import optimizelyConstants, {
  CLICK_CO_ADDITIONAL_ADDRESS_INFO,
  ERROR_CO_FIELD_ERRORS,
} from 'constants/optimizely'
import settingsSelectors from 'features/account/settings/duck/selectors'
import { CHECKOUT_TRACKING_ERROR_TYPES } from 'features/checkout/constants'
import { trackError } from 'features/checkout/duck/operations'
import checkoutSelectors from 'features/checkout/duck/selectors'
import { ErrorType, getFieldErrorMessage } from 'features/checkout/helpers/getFieldErrorMessage'
import { trackOptimizelyEvent } from 'helpers/optimizely'

import DateOfBirth from '../components/DateOfBirth'
import PhoneNumber, { PrefixSelect } from '../components/PhoneNumber/PhoneNumber'
import ToggleFieldsLink from '../components/ToggleFieldsLink/ToggleFieldsLink'
import styles from './FieldList.scss'
import { FieldErrorObject, FieldListProps } from './types'
import { getMarketCode } from 'App/duck/selectors'
import { InformaticaAddressVerification } from '../components/InformaticaAddressVerification/InformaticaAddressVerification'

/**
 * Maps props from API to correct input/select.
 * Setup register callback for react-hook-form to handle state and validations.
 *
 * @param {Object[]} fields :: list of input fields
 */

const FieldList: React.FC<FieldListProps> = ({
  fields = [],
  useLabelFromStorage = true,
  onErrorChange,
  shouldUseSplitPhoneNumber = false,
  shouldFillInFullAddress,
  onValueChange,
  errorTrackingSection,
  nonMandatoryFieldsToHide,
  fieldsToShowSideBySide = new Set(),
  shouldShowHouseNumberPrompt = false,
  shouldUseInformaticaAddressSuggestions = false,
  onAddressSelection,
  // Used in experiment, remove on archive [COWEB-2977]
  newCopyForShowFieldExperiment,
  ...props
}) => {
  const postalCode = useSelector(checkoutSelectors.getPostalCode)
  const addressPostalCodeValue = useSelector(settingsSelectors.getPostalCode)
  const currentMarketCode = useSelector(getMarketCode)
  const path = window.location.pathname.includes('/settings/addressbook')
  const postalCodeValue = path ? addressPostalCodeValue : postalCode
  const {
    errors,
    defaultValues = {},
    formState,
    setValue,
    watch,
    trigger,
    getValues,
  } = useFormContext() as UseFormContextFixedType
  const touched = formState?.touched as Record<string, boolean>
  const touchedFields = Object.keys(touched)
  const country = (watch('country') || currentMarketCode) as string
  const line1Value = watch('line1')
  const dispatch = useDispatch()

  const hasFieldsToShowSideBySide = fieldsToShowSideBySide?.size > 0

  const initialFieldsToHide = useMemo(() => {
    if (nonMandatoryFieldsToHide) {
      return nonMandatoryFieldsToHide.reduce((acc, curr) => {
        const filteredFieldsTohide = curr.fieldsToHide.filter((f) =>
          fields.find((fieldToFind) => fieldToFind?.attribute === f && !fieldToFind.required),
        )
        if (curr.fieldsToHide) return [...acc, ...[...filteredFieldsTohide]]
        return [...acc]
      }, [])
    }
    return []
  }, [nonMandatoryFieldsToHide, fields])

  const [fieldsToHide, setFieldsToHide] = useState<string[]>(initialFieldsToHide)
  const [shouldDisplayHouseNumberPrompt, setShouldDisplayHouseNumberPrompt] = useState(false)

  const [{ enabled: shouldDisableAutoSuggestion }] = useDecision(
    optimizelyConstants.ACCOUNT_DISABLED_AUTO_SUGGESTION,
  )

  // PostalCode for Serbian market
  const [{ enabled: shouldEnablePostalCode }] = useDecision(optimizelyConstants.ACCOUNT_POSTAL_CODE)

  useEffect(() => {
    if (onErrorChange) {
      onErrorChange({ formErrors: errors, touchedFields, isSubmitting: formState.isSubmitting })
    }
    if (errorTrackingSection) {
      const errorInformation = Object.entries(errors) as [string, FieldErrorObject][]
      if (errorInformation?.length > 0) trackOptimizelyEvent(ERROR_CO_FIELD_ERRORS)

      errorInformation.forEach((error: [string, FieldErrorObject]) => {
        if (fields.find((field) => field?.attribute === error?.[0])) {
          const errorMessage = getFieldErrorMessage({
            type: error?.[1]?.type as ErrorType,
            fieldConfigName: '',
            fieldName: error?.[0],
          })
          dispatch(
            trackError({
              section: errorTrackingSection,
              message: errorMessage,
              id: `${error?.[1]?.type}_${error?.[0]}`,
              errorType: CHECKOUT_TRACKING_ERROR_TYPES.field,
            }),
          )
        }
      })
    }
  }, [JSON.stringify(errors)])

  // Function aims at setting postalCode for Serbia market
  useEffect(() => {
    if (fields.find((field) => field?.attribute === 'postalCode' && field?.disabled)) {
      setValue('postalCode', postalCodeValue || '', { shouldValidate: true })
    }
  }, [postalCodeValue])

  if (isEmpty(fields)) {
    return null
  }

  const handleToggleFieldClick = (attribute: string): void => {
    const fieldsToToggle = nonMandatoryFieldsToHide?.find((f) =>
      f.fieldsToHide.includes(attribute),
    )?.fieldsToHide
    if (!fieldsToToggle) return
    if (fieldsToHide.includes(attribute)) {
      setFieldsToHide(fieldsToHide.filter((hiddenFields) => !fieldsToToggle.includes(hiddenFields)))
      if (attribute === 'careOf' || attribute === 'line2') {
        trackOptimizelyEvent(CLICK_CO_ADDITIONAL_ADDRESS_INFO)
      }
    } else {
      fieldsToToggle.forEach((f) => f !== 'prefix' && setValue(f, ''))
      setFieldsToHide([...fieldsToHide, ...fieldsToToggle])
    }
  }

  const getAdditionalFieldProps = (
    field: FieldConfig,
    fieldIndex: number,
  ): Partial<FieldConfig> => {
    const {
      attribute,
      fieldType,
      autoComplete,
      helpText,
      placeholder,
      required,
      disabled,
      shouldShowValidationIcon,
      disableLabel,
      label,
      copyLabels,
      disableSecureCheck,
      shouldDisableValidation,
      shouldEnableInputAutoFocus,
      validation,
    } = field

    const { format } = validation || {}
    const isDropdownField =
      fieldType.name === 'DROPDOWN' || fieldType.name === 'SELECT' || fieldType.name === 'COMBOBOX'
    const isValidHalfField =
      hasFieldsToShowSideBySide &&
      fieldsToShowSideBySide.has(attribute) &&
      (fieldsToShowSideBySide.has(fields?.[fieldIndex + 1]?.attribute as string) ||
        (fieldsToShowSideBySide.has(fields?.[fieldIndex - 1]?.attribute as string) &&
          (!fields?.[fieldIndex - 2] ||
            !fieldsToShowSideBySide.has(fields?.[fieldIndex - 2]?.attribute as string))))
    return {
      required,
      autoComplete,
      label:
        typeof label === 'string'
          ? useLabelFromStorage
            ? getLabelFromStorage(label)
            : label
          : label,
      name: attribute,
      type: fieldType.name.toLowerCase() as 'number' | 'search' | 'text' | 'tel' | 'date',
      hint: helpText && getLabelFromStorage(helpText, { replacements: format }),
      placeholder: placeholder && getLabelFromStorage(placeholder),
      ...(copyLabels && { copyLabels }),
      className: cn([
        styles.input,
        hasFieldsToShowSideBySide
          ? isValidHalfField && !isDropdownField
            ? styles.halfWidthField
            : styles.fullWidthField
          : '',
      ]),
      ...(disabled && { disabled }),
      ...(shouldShowValidationIcon && { shouldShowValidationIcon }),
      ...(disableLabel && { disableLabel }),
      ...(disableSecureCheck && { disableSecureCheck }),
      ...(shouldDisableValidation && { shouldDisableValidation }),
      ...(shouldEnableInputAutoFocus && { shouldEnableInputAutoFocus }),
      ...(shouldFillInFullAddress && { shouldFillInFullAddress }),
    }
  }

  const allFields = fields.map((field, fieldIndex) => {
    const {
      validation,
      selectOptions,
      errorMessages,
      onBlurHandler,
      onChangeHandler = () => {},
      passwordMaxLength,
      shouldDisableGoogleAutoComplete,
      attribute,
      fieldType,
      required,
      disableSecureCheck,
      copyLabels,
      disabled,
    } = field || {}

    const { format, minLength, maxLength, jsPattern, validate } = validation || {}

    let element: JSX.Element

    const additionalFieldProps = field ? getAdditionalFieldProps(field, fieldIndex) : {}

    const currentFieldToHide =
      attribute &&
      !required &&
      nonMandatoryFieldsToHide?.find((f) => f.fieldsToHide.includes(attribute))
    const fieldShouldBeHidden = !!currentFieldToHide

    let validationRules: RegisterOptions = {
      ...(jsPattern && {
        pattern: {
          value: new RegExp(jsPattern),
          message:
            (useLabelFromStorage
              ? getLabelFromStorage(errorMessages?.pattern || '', { replacements: format })
              : errorMessages?.pattern) || '',
        },
      }),
      ...(maxLength && {
        maxLength: {
          value: maxLength,
          message:
            (useLabelFromStorage
              ? getLabelFromStorage(errorMessages?.maxLength || '', {
                  replacements: format,
                })
              : errorMessages?.maxLength) || '',
        },
      }),
      ...(minLength && {
        minLength: {
          value: minLength,
          message:
            (useLabelFromStorage
              ? getLabelFromStorage(errorMessages?.minLength || '', {
                  replacements: format,
                })
              : errorMessages?.minLength) || '',
        },
      }),
      required:
        required &&
        (useLabelFromStorage
          ? getLabelFromStorage(errorMessages?.required || '', { replacements: format })
          : errorMessages?.required),
      ...(validate && { validate }),
    }

    let defaultValue = (attribute && defaultValues?.[attribute]) || ''

    const currentFieldValue = attribute && (getValues(attribute) as string)
    if (
      fieldShouldBeHidden &&
      fieldsToHide.includes(attribute) &&
      !!currentFieldValue &&
      attribute !== 'prefix'
    ) {
      setFieldsToHide(fieldsToHide.filter((f) => !currentFieldToHide.fieldsToHide.includes(f)))
    }

    if (!field) {
      element = <></>
    } else if (
      fieldType.name === 'DROPDOWN' ||
      fieldType.name === 'SELECT' ||
      fieldType.name === 'COMBOBOX'
    ) {
      defaultValue = ''
      element = <Select options={selectOptions || []} {...additionalFieldProps} />
    } else if (fieldType.name === 'DATE') {
      element = <DateOfBirth {...additionalFieldProps} />
    } else if (attribute === 'prefix') {
      if (shouldUseSplitPhoneNumber) {
        validationRules = {}
        const cellPhone = fields.find((f) => f?.attribute === 'cellPhone')
        element = (
          <PrefixSelect cellPhoneCopyKey={cellPhone?.label || ''} {...additionalFieldProps} />
        )
      } else {
        return null
      }
    } else if (attribute === 'vat') {
      validationRules = {
        validate,
      }
      element = <Input {...additionalFieldProps} />
    } else if (attribute === 'cellPhone') {
      validationRules = {
        ...(validate && {
          validate: (params) => validate(watch as any, params),
        }),
        required:
          required && getLabelFromStorage(errorMessages?.required || '', { replacements: format }),
      }
      if (shouldUseSplitPhoneNumber) {
        element = <PhoneNumber trigger={trigger} {...additionalFieldProps} />
      } else {
        element = (
          <PhoneNumberInput
            defaultCountry={currentMarketCode}
            lang={getLocale() as any}
            {...additionalFieldProps}
          />
        )
      }
    } else if (attribute === 'line1' && !shouldDisableGoogleAutoComplete) {
      if (shouldUseInformaticaAddressSuggestions) {
        element = <InformaticaAddressVerification {...additionalFieldProps} country={country} />
      } else {
        element = (
          <AddressAutocomplete
            {...additionalFieldProps}
            country={country}
            key={`AddressAutocomplete-${country}-${field.addressFormat}`}
            isFieldWithoutMap
            addressFormat={field.addressFormat}
            isAutoSuggestionDisabled={shouldDisableAutoSuggestion}
            onAddressSelectionStart={onAddressSelection}
          />
        )
      }
    } else if (attribute === 'password') {
      if (!disableSecureCheck) {
        validationRules = {
          validate(value) {
            if (!getIsValidPassword(value)) {
              return 'Invalid'
            }

            return true
          },
        }
      } else {
        validationRules = {
          ...validationRules,
          validate(value) {
            getIsValidPassword(value)
            return true
          },
        }
      }
      element = (
        <PasswordInput
          {...additionalFieldProps}
          copyLabels={copyLabels as PasswordInputLabels}
          disablePasswordMeter={!IS_PASSWORD_METER_ENABLED}
          maxLength={passwordMaxLength && passwordMaxLength}
        />
      )
    } else if (fieldType.name === 'CHECKBOX') {
      defaultValue = (attribute && defaultValues?.[attribute]) || ''
      element = (
        <CheckBoxInput label="I am agree to subscribe to newsletter" {...additionalFieldProps} />
      )
      // Used in markets where the postalCode is readOnly/disabled so postalCode is fetched through api and not user input
    } else if (attribute === 'postalCode' && disabled) {
      validationRules = {}
      const { province: provinceValue, town: townValue } = getValues(['province', 'town'])
      if (!postalCodeValue) {
        if (!townValue || !provinceValue) {
          element = <></>
        } else {
          element = (
            <div className={styles.readOnlyPostalCodeSkeleton}>
              <RectangleSkeleton width="100px" height="20px" />
              <RectangleSkeleton width="60px" height="15px" />
            </div>
          )
        }
      } else if (path && shouldEnablePostalCode) {
        element = <Input {...additionalFieldProps} />
      } else {
        element = (
          <>
            <BodyText>{getLabelFromStorage('CHECKOUTADDRESS.postalCode.label.text')}</BodyText>
            <BodyText isMarginless>{postalCodeValue}</BodyText>
          </>
        )
      }
    } else {
      element = <Input {...additionalFieldProps} />
    }

    return (
      <FieldController
        {...props}
        key={attribute}
        render={({ onChange, onBlur, value, ...rest }) => {
          const handleBlur = (event): void => {
            const { value: targetValue } = event.target
            onBlur(targetValue)
            if (onValueChange) {
              onValueChange([attribute, value])
            }
            if (shouldShowHouseNumberPrompt && attribute === 'line1') {
              if (!/\d/.test(line1Value as string) && !!line1Value) {
                setShouldDisplayHouseNumberPrompt(true)
              } else {
                setShouldDisplayHouseNumberPrompt(false)
              }
            }
            // Currently the BE validation regex doesn't accept leading/training spaces (which it should)
            // but we can't change that as it's being used in the old (Angular/Hybris) checkout as well as on the BE for validation
            // So to be able to submit the form, we need to trim the value so it passes the regex validatiion.
            if (attribute === 'postalCode') {
              setValue(attribute, targetValue.trim(), { shouldValidate: true })
            } else if (attribute === 'dateOfBirth') {
              trigger(attribute)
            }
          }

          const handleChange = (event: string): void => {
            if (attribute === 'cellPhone' && !!touched?.cellPhone && validate) {
              touched.cellPhone = false
            }
            if (shouldUseSplitPhoneNumber && attribute === 'cellPhone') {
              onChange(event && event.replace(/\D/g, ''))
            } else {
              onChange(event)
            }
            if (onValueChange && attribute === 'gender') {
              onValueChange([attribute, value])
            }
          }

          const propsToPass: any = {
            onBlur: attribute === 'vat' ? onBlurHandler : handleBlur,
            onChange: handleChange,
            ...rest,
          }

          if (fieldType.name === 'CHECKBOX') {
            propsToPass.checked = value
          } else {
            propsToPass.value = value
          }

          if (attribute === 'vatInvoiceType') {
            propsToPass.onChange = onChangeHandler
            propsToPass.value = field?.value
          }

          if ((attribute === 'province' || attribute === 'town') && fieldType.name === 'COMBOBOX') {
            propsToPass.onChange = (selectedValue: string) => {
              const attributesToReset =
                attribute === 'province' ? ['town', 'district'] : ['district']
              attributesToReset.forEach((key) => {
                const fieldToClear = fields.find((f) => f?.attribute === key)
                if (fieldToClear?.uiType === 'COMBOBOX') setValue(key, '')
              })
              setValue(attribute, selectedValue, { shouldValidate: true })
              if (selectedValue) {
                onChangeHandler(selectedValue)
              }
            }
          }

          // Reset and invalidate select field if user has old & invalid saved value
          if (
            (attribute === 'province' || attribute === 'town' || attribute === 'district') &&
            fieldType.name === 'COMBOBOX'
          ) {
            const fieldValue = getValues(attribute) as string
            const doesUserHaveMismatchingValue =
              fieldValue &&
              !!selectOptions?.length &&
              selectOptions.length > 1 &&
              !selectOptions.some((selectOption) => selectOption?.value === fieldValue)
            if (doesUserHaveMismatchingValue) {
              setValue(attribute, '', { shouldValidate: true })
            }
          }

          if (attribute === 'bankName' && fieldType.name === 'DROPDOWN') {
            propsToPass.onChange = (selectedValue: string) => {
              setValue('branchName', '')
              setValue(attribute, selectedValue, { shouldValidate: true })
              if (selectedValue) {
                onChangeHandler(selectedValue)
              }
            }
          }

          if (fieldShouldBeHidden) {
            const inputFromFieldAttribute = React.cloneElement(element, {
              ...propsToPass,
              ...{ className: styles.collapsedInput },
            })
            const hasSingleLinePhoneField = attribute === 'cellPhone' && !shouldUseSplitPhoneNumber

            const allFieldsThatShouldBeHidden = fields.filter(
              (f) => f?.attribute && currentFieldToHide.fieldsToHide.includes(f.attribute),
            )
            const isFirstFieldTohide =
              allFieldsThatShouldBeHidden?.[0]?.attribute === attribute || hasSingleLinePhoneField

            return (
              <div className={styles.toggledFieldsContainer}>
                {isFirstFieldTohide && (
                  <ToggleFieldsLink
                    onClick={() => handleToggleFieldClick(attribute)}
                    shouldHideField={fieldsToHide.includes(attribute)}
                    showLabel={newCopyForShowFieldExperiment || currentFieldToHide.showLabel}
                    hideLabel={currentFieldToHide.hideLabel}
                  />
                )}
                <Collapse
                  isExpanded={!fieldsToHide.includes(attribute)}
                  controlled
                  toggleClassNames={[styles.toggleHide]}
                  shouldHideHeaderWhenExpanded
                >
                  {inputFromFieldAttribute}
                </Collapse>
              </div>
            )
          }

          if (shouldDisplayHouseNumberPrompt && attribute === 'line1') {
            const line1Element = React.cloneElement(element, {
              ...propsToPass,
              ...{ className: styles.inputWithHouseNumberPrompt },
            })
            return (
              <>
                {line1Element}
                <div className={styles.houseNumberPrompt}>
                  <InfoIcon />
                  <BodyText>{getLabelFromStorage('text.account.address1.sub1')}</BodyText>
                </div>
              </>
            )
          }

          return React.cloneElement(element, propsToPass)
        }}
        rules={validationRules}
        name={attribute}
        defaultValue={defaultValue}
      />
    )
  })
  return hasFieldsToShowSideBySide ? (
    <div className={styles.fieldsContainer}>{allFields}</div>
  ) : (
    <>{allFields}</>
  )
}

export default React.memo(FieldList)
